import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import DocumentHeader from 'Components/DocumentHeader';
import DocumentWrapper from 'Components/DocumentWrapper';
import HistoryRow from 'Components/HistoryRow';
import HistoryRowControls from 'Components/HistoryRowControls';
import HistorySection from 'Components/HistorySection';
import FormEditingHandler from 'Containers/FormEditingHandler';
import { findLatest, isPartialDate, sortPartialDate } from 'neuro-utils';
import { stateToDietProps } from '../utils';
import DietTherapyForm from './Form';
import DietHistory from './HistoryRowData/index';
import DocumentCreationButton from 'Components/DocumentCreationButton';
import { isEnded } from 'Utility/isEnded';
import { formatPartialDate } from 'Utility/date';

const activeDietTherapyHistory = (
  documents: IDocumentWithTypeV2<'diet'>[],
  startEdit: StartEdit,
  view?: IView,
): React.JSX.Element | undefined => {
  return documents.length > 0 ? (
    <HistorySection headerText={<FormattedMessage id="dietTherapy.activeDiet" />} hasHistoryRows={true}>
      {documents
        .toSorted((n1, n2) => sortPartialDate(n2.startDate, n1.startDate))
        .map((d, i) => (
          <HistoryRow
            headerText={
              d.startDate
                ? formatPartialDate(d.startDate) + ' - ' + (d.endDate ? formatPartialDate(d.endDate) : '')
                : undefined
            }
            key={i + 'active'}
            rowButton={<HistoryRowControls document={d} startEdit={startEdit} />}
            controlsMargin={false}
          >
            <DietHistory document={d} startEdit={startEdit} view={view} />
          </HistoryRow>
        ))}
    </HistorySection>
  ) : undefined;
};

const endedDietTherapyHistory = (
  documents: IDocumentWithTypeV2<'diet'>[],
  startEdit: StartEdit,
  view?: IView,
): React.JSX.Element | undefined => {
  return documents.length > 0 ? (
    <HistorySection
      headerText={<FormattedMessage id="dietTherapy.endedDietTherapy" />}
      inactive={true}
      hasHistoryRows={true}
    >
      {documents
        .toSorted((n1, n2) => sortPartialDate(n2.endDate, n1.endDate))
        .map((d, i) => (
          <HistoryRow
            headerText={
              d.startDate
                ? formatPartialDate(d.startDate) + ' - ' + (d.endDate ? formatPartialDate(d.endDate) : '')
                : undefined
            }
            key={i + 'active'}
            rowButton={<HistoryRowControls document={d} startEdit={startEdit} />}
            controlsMargin={false}
          >
            <DietHistory document={d} startEdit={startEdit} view={view} />
          </HistoryRow>
        ))}
    </HistorySection>
  ) : undefined;
};

const _DietTherapy = ({ documents, visitreason }: IDietProps): React.JSX.Element => {
  // conditions for restricted operations
  const isRetrospective = visitreason === 'retrospectiveDataTransfer';
  const isOngoingDiet = !!findLatest<IDocumentWithTypeV2<'diet'> & { date?: PartialDate | null }>(
    documents.map(d => ({ ...d, date: d.startDate })),
    diet => !isPartialDate(diet.endDate),
    { singleton: true },
  );
  return (
    <FormEditingHandler name="dietTherapy" documents={documents}>
      {(editing, startEdit, formData, view): React.JSX.Element => (
        <DocumentWrapper>
          <DocumentHeader
            name={'dietTherapy'}
            headerId={'dietTherapy.title'}
            editing={editing}
            editButtons={
              <>
                {!view?.viewing && (
                  <DocumentCreationButton
                    name="diet"
                    text={'general.new'}
                    onClick={startEdit({}, 'diet')}
                    disabled={!isRetrospective && isOngoingDiet}
                    disabledTooltip={<FormattedMessage id="dietTherapy.endOngoingToAddNew" />}
                  />
                )}
              </>
            }
          />
          {editing ? (
            <DietTherapyForm
              formData={formData}
              document={documents.find(d => d._id === editing)}
              editing={editing}
              documents={documents}
            />
          ) : null}

          {view?.viewing ? (
            <DietTherapyForm
              formData={formData}
              document={documents.find(d => d._id === view.viewing)}
              viewing={!!view.viewing}
            />
          ) : null}

          {!editing && !view?.viewing ? (
            <div>
              {activeDietTherapyHistory(
                documents.filter(d => !isEnded(d.endDate)),
                startEdit,
                view,
              )}
              {endedDietTherapyHistory(
                documents.filter(d => isEnded(d.endDate)),
                startEdit,
                view,
              )}
            </div>
          ) : undefined}
        </DocumentWrapper>
      )}
    </FormEditingHandler>
  );
};
const DietTherapy = connect(stateToDietProps)(_DietTherapy);

interface IDietProps extends Pick<IJWTBody, 'visitreason'> {
  documents: IDocumentWithTypeV2<'diet'>[];
}

export default DietTherapy;
